/**
 * RKL / EntryDetail — single kill list entry with verify/execute/cancel actions.
 *
 * Role-gated: only SuperAdmin + Supervisor can invoke mutations. FSRs see
 * read-only detail.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import type { KillListEntryDto } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button, Pill } from '@/components/primitives';
import { formatLocal } from '@/utils/time';
import { VerifyDrawer } from './VerifyDrawer';
import { ExecuteConfirmation } from './ExecuteConfirmation';

export function EntryDetail({ entryId }: { entryId: string }): JSX.Element {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const [entry, setEntry] = useState<KillListEntryDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showVerify, setShowVerify] = useState<boolean>(false);
  const [showExecute, setShowExecute] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [showCancel, setShowCancel] = useState<boolean>(false);
  const abortRef = useRef<AbortController | null>(null);

  const canMutate = user?.primaryRole === 'SuperAdmin' || user?.primaryRole === 'Supervisor';

  const load = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.retail.killList.get(entryId, { signal: controller.signal });
      if (controller.signal.aborted) return;
      if (!resp.success) {
        setError(resp.error ?? 'Failed to load entry');
        setEntry(null);
      } else {
        setEntry(resp.entry ?? null);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load entry');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [apiClient, entryId]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  const onVerify = useCallback(
    async (note: string): Promise<void> => {
      try {
        await apiClient.retail.killList.verify(entryId, {
          verifiedByEmail: user?.upn ?? 'unknown@mypcb.com',
          approvalNote: note,
        });
        void apiClient.retail.events
          .write({
            eventType: 'killlist-entry-verified',
            eventCategory: 'system',
            severity: 'info',
            actor: user?.upn ?? 'unknown',
            payloadJson: JSON.stringify({ entryId, note }),
          })
          .catch(() => undefined);
        setShowVerify(false);
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Verify failed');
      }
    },
    [apiClient, entryId, load, user],
  );

  const onExecute = useCallback(async (): Promise<void> => {
    try {
      await apiClient.retail.killList.execute(entryId, {
        executedByEmail: user?.upn ?? 'unknown@mypcb.com',
      });
      void apiClient.retail.events
        .write({
          eventType: 'killlist-entry-executed',
          eventCategory: 'system',
          severity: 'warn',
          actor: user?.upn ?? 'unknown',
          payloadJson: JSON.stringify({ entryId }),
        })
        .catch(() => undefined);
      setShowExecute(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Execute failed');
    }
  }, [apiClient, entryId, load, user]);

  const onCancel = useCallback(async (): Promise<void> => {
    const reason = cancelReason.trim();
    if (!reason) return;
    try {
      await apiClient.retail.killList.cancel(entryId, {
        cancelledByEmail: user?.upn ?? 'unknown@mypcb.com',
        reason,
      });
      void apiClient.retail.events
        .write({
          eventType: 'killlist-entry-cancelled',
          eventCategory: 'system',
          severity: 'info',
          actor: user?.upn ?? 'unknown',
          payloadJson: JSON.stringify({ entryId, reason }),
        })
        .catch(() => undefined);
      setShowCancel(false);
      setCancelReason('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cancel failed');
    }
  }, [apiClient, cancelReason, entryId, load, user]);

  return (
    <div>
      <Link
        to="/rkl"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          fontSize: 12,
          color: 'var(--pcb-blue)',
          textDecoration: 'none',
          marginBottom: 'var(--sp-4)',
        }}
      >
        <ChevronLeft size={14} /> Back to Kill List
      </Link>

      {loading && <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>Loading…</div>}

      {error && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            marginBottom: 'var(--sp-5)',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      {entry && (
        <>
          <header style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)', marginBottom: 'var(--sp-6)' }}>
            <h1 style={{ margin: 0 }}>Kill entry {entry.killListEntryId.slice(0, 8)}…</h1>
            <Pill variant={entry.status === 'executed' ? 'ok' : entry.status === 'cancelled' ? 'muted' : 'warn'}>
              {entry.status}
            </Pill>
            {entry.testMode && <Pill variant="warn">Test Mode</Pill>}
          </header>

          <section
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--surface-border)',
              borderRadius: 'var(--r-md)',
              padding: 'var(--sp-7)',
              marginBottom: 'var(--sp-6)',
            }}
          >
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: 'var(--sp-3) var(--sp-6)',
                fontSize: 13,
              }}
            >
              <dt style={{ color: 'var(--ink-500)' }}>Entry type</dt>
              <dd style={{ margin: 0 }}>{entry.entryType}</dd>
              <dt style={{ color: 'var(--ink-500)' }}>Target</dt>
              <dd style={{ margin: 0, fontFamily: 'var(--font-mono)' }}>{entry.targetId}</dd>
              <dt style={{ color: 'var(--ink-500)' }}>Reason</dt>
              <dd style={{ margin: 0 }}>{entry.reason}</dd>
              <dt style={{ color: 'var(--ink-500)' }}>Source cart</dt>
              <dd style={{ margin: 0, fontFamily: 'var(--font-mono)' }}>{entry.sourceCartId ?? '—'}</dd>
              <dt style={{ color: 'var(--ink-500)' }}>Created</dt>
              <dd style={{ margin: 0 }}>
                {formatLocal(entry.createdAt)} by {entry.createdBy}
              </dd>
              {entry.verifiedAt && (
                <>
                  <dt style={{ color: 'var(--ink-500)' }}>Verified</dt>
                  <dd style={{ margin: 0 }}>
                    {formatLocal(entry.verifiedAt)} by {entry.verifiedBy ?? '—'}
                  </dd>
                </>
              )}
              {entry.executedAt && (
                <>
                  <dt style={{ color: 'var(--ink-500)' }}>Executed</dt>
                  <dd style={{ margin: 0 }}>
                    {formatLocal(entry.executedAt)} by {entry.executedBy ?? '—'}
                  </dd>
                </>
              )}
              {entry.notes && (
                <>
                  <dt style={{ color: 'var(--ink-500)' }}>Notes</dt>
                  <dd style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{entry.notes}</dd>
                </>
              )}
              {entry.approvalNote && (
                <>
                  <dt style={{ color: 'var(--ink-500)' }}>Approval note</dt>
                  <dd style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{entry.approvalNote}</dd>
                </>
              )}
            </dl>
          </section>

          <div style={{ display: 'flex', gap: 'var(--sp-4)' }}>
            {canMutate && entry.status === 'pending-verification' && !entry.testMode && (
              <Button variant="primary" onClick={() => setShowVerify(true)}>
                Verify
              </Button>
            )}
            {canMutate && (entry.status === 'verified' || (entry.testMode && entry.status !== 'executed')) && (
              <Button variant="danger" onClick={() => setShowExecute(true)}>
                Execute
              </Button>
            )}
            {canMutate &&
              entry.status !== 'executed' &&
              entry.status !== 'cancelled' && (
                <Button variant="ghost" onClick={() => setShowCancel((v) => !v)}>
                  Cancel entry
                </Button>
              )}
            {!canMutate && <Pill variant="muted">Read-only — requires System Ops role</Pill>}
          </div>

          {showCancel && (
            <div
              style={{
                marginTop: 'var(--sp-6)',
                padding: 'var(--sp-5)',
                background: 'var(--ink-50)',
                borderRadius: 'var(--r-md)',
              }}
            >
              <label
                htmlFor="kl-cancel-reason"
                style={{
                  display: 'block',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  color: 'var(--ink-500)',
                  marginBottom: 'var(--sp-3)',
                }}
              >
                Cancel reason (required)
              </label>
              <textarea
                id="kl-cancel-reason"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: 'var(--sp-4)',
                  border: '1px solid var(--ink-300)',
                  borderRadius: 'var(--r-md)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 13,
                }}
              />
              <div style={{ display: 'flex', gap: 'var(--sp-3)', marginTop: 'var(--sp-4)' }}>
                <Button variant="ghost" onClick={() => setShowCancel(false)}>
                  Never mind
                </Button>
                <Button variant="danger" onClick={() => void onCancel()} disabled={!cancelReason.trim()}>
                  Cancel entry
                </Button>
              </div>
            </div>
          )}

          {showVerify && (
            <VerifyDrawer
              entry={entry}
              onClose={() => setShowVerify(false)}
              onConfirm={(note) => void onVerify(note)}
            />
          )}

          {showExecute && (
            <ExecuteConfirmation
              entry={entry}
              onClose={() => setShowExecute(false)}
              onConfirm={() => void onExecute()}
            />
          )}
        </>
      )}
    </div>
  );
}
